class Bh26ImageFanCards extends HTMLElement {
  constructor() {
    super();
    this.onIntersect = this.onIntersect.bind(this);
    this.onWindowLoad = this.onWindowLoad.bind(this);
    this.playAnimation = this.playAnimation.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.items = Array.from(this.querySelectorAll('[data-bh26-ifc-item]'));

    if (!this.items.length) return;

    const duration = parseInt(this.dataset.animationDuration || '900', 10);
    this.style.setProperty('--bh26-ifc-animation-duration', `${duration}ms`);

    if (this.dataset.enableAnimation !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('is-ready');
      return;
    }

    this.primeAnimationState();

    if (window.Shopify && window.Shopify.designMode) {
      this.schedulePlay(40);
      return;
    }

    if (this.isElementInViewport()) {
      this.scheduleInitialLoadAnimation();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.schedulePlay(40);
      return;
    }

    this.observer = new IntersectionObserver(this.onIntersect, {
      threshold: 0.01,
      rootMargin: '0px 0px -5% 0px'
    });

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();

    if (this.loadTimer) {
      window.clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }

    window.removeEventListener('load', this.onWindowLoad);
  }

  isElementInViewport() {
    const rect = this.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  primeAnimationState() {
    if (this.primed) return;
    this.primed = true;

    const stagger = parseInt(this.dataset.animationStagger || '90', 10);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const midpoint = (this.items.length - 1) / 2;
    const animationOrder = this.items
      .map((_, index) => index)
      .sort((a, b) => {
        const aDistance = Math.abs(a - midpoint);
        const bDistance = Math.abs(b - midpoint);

        if (aDistance !== bDistance) return aDistance - bDistance;
        return a - b;
      });

    this.items.forEach((item, index) => {
      const spread = index - midpoint;
      const sequenceIndex = animationOrder.indexOf(index);
      const tiltDelta = spread === 0 ? 0 : (spread < 0 ? -8 : 8);
      const depthOffset = isMobile ? 32 : 72;
      const horizontalOffset = isMobile ? spread * 10 : spread * 22;

      item.style.setProperty('--bh26-ifc-entry-x', `${horizontalOffset}px`);
      item.style.setProperty('--bh26-ifc-entry-y', `${depthOffset}px`);
      item.style.setProperty('--bh26-ifc-entry-rotate', `${tiltDelta}deg`);
      item.style.transitionDelay = `${sequenceIndex * stagger}ms`;
    });

    this.classList.add('is-primed');
    void this.items[0].offsetHeight;
  }

  scheduleInitialLoadAnimation() {
    if (this.playScheduled || this.animated) return;
    this.playScheduled = true;

    if (document.readyState !== 'complete') {
      window.addEventListener('load', this.onWindowLoad, { once: true });
    }

    this.schedulePlay(document.readyState === 'complete' ? 40 : 120);
  }

  schedulePlay(delay = 0) {
    if (this.animated) return;

    if (this.loadTimer) {
      window.clearTimeout(this.loadTimer);
    }

    this.loadTimer = window.setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.playAnimation();
        });
      });
    }, delay);
  }

  onWindowLoad() {
    this.schedulePlay(60);
  }

  onIntersect(entries) {
    const entry = entries[0];

    if (!entry?.isIntersecting) return;

    this.schedulePlay(40);
    this.observer?.disconnect();
  }

  playAnimation() {
    if (this.animated) return;
    this.animated = true;

    if (this.loadTimer) {
      window.clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }

    window.removeEventListener('load', this.onWindowLoad);

    requestAnimationFrame(() => {
      this.classList.add('is-ready');
    });
  }
}

if (!customElements.get('bh26-image-fan-cards')) {
  customElements.define('bh26-image-fan-cards', Bh26ImageFanCards);
}
